/**
 * Popovers toggle the visibility of information or controls in a contextual overlay
 * @see WAI-ARIA https://www.w3.org/TR/wai-aria-practices-1.1/#disclosure
 */
import React, { ReactNode, RefObject, Component, createRef } from "react";
import { createPortal } from "react-dom";
import { Transition } from "react-transition-group";
import observeRect from "@reach/observe-rect";
import classNames from "classnames";
import getOverlayPosition, { OverlayPositionProps } from "../../../utils/getOverlayPosition";
import getPortalRoot from "../../../utils/getPortalRoot";
import forceReflow from "../../../utils/forceReflow";
import { KeyType, randomString } from "../../../utils";
import { AnimationDuration } from "../../../styles/Animations";
import Panel, { PanelTheme } from "../panel/Panel/Panel";
import { handleMouseUpByBlurring } from "../../../utils/focus";
import { observer } from "mobx-react";
import { action, computed, observable } from "mobx";
import { TransitionStatus } from "react-transition-group/Transition";

export enum PopoverPosition {
  Top = "top",
  Right = "right",
  Bottom = "bottom",
  Left = "left",
}

export enum PopoverTheme {
  Dark = "dark",
  Light = "light",
}

export enum PopoverSize {
  Small = "small",
  Medium = "medium",
  Large = "large",
  /**
   * Use for custom sized popovers
   */
  Auto = "auto",
}

export interface PopoverButtonProps {
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
  ref: RefObject<HTMLElement>;
  "aria-controls": string;
  "aria-expanded": boolean;
}

export interface PopoverProps {
  /**
   * Use this prop to convert to a controlled component
   */
  isOpen?: boolean;
  /**
   * A function returning a React element. Contains render props
   * to handle events and a11y attributes
   */
  button: (buttonProps: PopoverButtonProps) => ReactNode;
  /**
   * Handle updating state for controlled Popovers
   */
  onChange?: (isOpen: boolean) => void;
  /**
   * The preferred position. Will be overridden if this position
   * falls outside the viewport
   */
  position?: PopoverPosition;
  /**
   * Determines the width of the Popover's Panel
   */
  size?: PopoverSize;
  /**
   * The amount of space between the popover and button
   */
  offset?: number;
  /**
   * Adds a caret pointing at the trigger button
   */
  caret?: boolean;
  /**
   * Dark or light theme
   */
  theme?: PopoverTheme;
  /**
   * Overrides the randomly generated internal popover id
   */
  id?: string;
  maxHeight?: number;
  className?: string;
  children?: ReactNode;
}

@observer
class Popover extends Component<PopoverProps> {
  public static defaultProps = {
    theme: PopoverTheme.Light,
    size: PopoverSize.Medium,
    position: PopoverPosition.Bottom,
    offset: 4,
  };

  @observable.ref
  private internalIsOpen = false;

  @observable.ref
  private keyboardTriggered = false;

  private buttonRef = createRef<HTMLButtonElement>();
  private portalRoot = getPortalRoot("popover-root");
  private randomId = randomString();

  @computed
  private get popoverId() {
    return this.props.id || `popover-${this.randomId}`;
  }

  @computed
  private get isOpen() {
    return this.props.isOpen ?? this.internalIsOpen;
  }

  @computed
  private get caretSize() {
    return this.props.caret ? 14 : 0;
  }

  @action
  private setIsOpen = (isOpen: boolean) => {
    this.props.onChange?.(isOpen);
    this.internalIsOpen = isOpen;
  };

  @action
  private handleButtonClick = () => {
    this.setIsOpen(!this.isOpen);
  };

  @action
  private handleButtonKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === KeyType.Enter) {
      this.keyboardTriggered = true;
    }
  };

  @action
  private handleExited = () => {
    this.keyboardTriggered = false;
  };

  public render(): ReactNode {
    const { button, children, size, className, theme, offset, position } = this.props;

    return (
      <>
        {button({
          ref: this.buttonRef,
          ["aria-controls"]: this.popoverId,
          ["aria-expanded"]: this.isOpen,
          onClick: this.handleButtonClick,
          onKeyDown: this.handleButtonKeyDown,
        })}
        <Transition
          mountOnEnter
          unmountOnExit
          in={this.isOpen}
          timeout={AnimationDuration.Medium}
          onExited={this.handleExited}
          onEnter={forceReflow}
        >
          {(status) =>
            createPortal(
              <PopoverPanel
                className={className}
                id={this.popoverId}
                theme={theme}
                size={size}
                position={position}
                caretSize={this.caretSize}
                setIsOpen={this.setIsOpen}
                status={status}
                buttonRef={this.buttonRef}
                offset={offset}
                keyboardTriggered={this.keyboardTriggered}
                maxHeight={this.props.maxHeight}
              >
                {children}
              </PopoverPanel>,
              this.portalRoot as Element,
            )
          }
        </Transition>
      </>
    );
  }
}

interface PopoverPanelProps {
  className?: string;
  id: string;
  theme?: PopoverTheme;
  size?: PopoverSize;
  position?: PopoverPosition;
  caretSize: number;
  children?: ReactNode;
  status?: TransitionStatus;
  buttonRef: RefObject<HTMLButtonElement>;
  setIsOpen: (isOpen: boolean) => void;
  offset?: number;
  maxHeight?: number;
  keyboardTriggered: boolean;
}

@observer
class PopoverPanel extends Component<PopoverPanelProps> {
  @observable.ref
  private popoverPosition?: OverlayPositionProps;

  private popoverRef = createRef<HTMLDivElement>();
  private rectObserver?: { observe(): void; unobserve(): void };

  public componentDidMount(): void {
    this.rectObserver = observeRect(this.props.buttonRef.current as HTMLElement, this.handleTargetRectChange);

    this.rectObserver?.observe();
    document.addEventListener("mousedown", this.handleOuterClick);
    document.addEventListener("keydown", this.handleKeyDown);

    // Manage keyboard focus
    if (this.props.keyboardTriggered) {
      this.popoverRef.current?.focus();
    }
  }

  public componentWillUnmount(): void {
    this.rectObserver?.unobserve();
    document.removeEventListener("mousedown", this.handleOuterClick);
    document.removeEventListener("keydown", this.handleKeyDown);

    // Restore focus
    if (this.props.keyboardTriggered) {
      this.props.buttonRef.current?.focus();
    }
  }

  public componentDidUpdate(prevProps: PopoverPanelProps): void {
    if (
      this.props.buttonRef.current &&
      (prevProps.size !== this.props.size ||
        prevProps.caretSize !== this.props.caretSize ||
        prevProps.offset !== this.props.offset)
    ) {
      this.handleTargetRectChange(this.props.buttonRef.current?.getBoundingClientRect());
    }
  }

  @computed
  private get position() {
    return this.popoverPosition?.modifier || this.props.position;
  }

  @action
  private handleTargetRectChange = (targetRect: DOMRect) => {
    const { caretSize, position, offset } = this.props;
    const overlayRect = this.popoverRef.current?.getBoundingClientRect();

    const newPopoverPosition = getOverlayPosition({
      position,
      targetRect,
      overlayRect,
      offset: offset || 0 - caretSize + (caretSize * Math.sqrt(2)) / 2,
      caretSize: caretSize,
      outerMargin: 16,
    });

    this.popoverPosition = newPopoverPosition;
  };

  @action
  private handleOuterClick = (event: MouseEvent) => {
    const target = event.target as Node;

    if (
      target !== this.popoverRef.current &&
      target !== this.props.buttonRef.current &&
      !this.popoverRef.current?.contains(target) &&
      !this.props.buttonRef.current?.contains(target)
    ) {
      this.props.setIsOpen(false);
    }
  };

  @action
  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === KeyType.Escape) {
      this.props.setIsOpen(false);
    }
  };

  public render(): ReactNode {
    const { className, id, theme, size, caretSize, children, status } = this.props;

    return (
      <div
        ref={this.popoverRef}
        className={classNames("popover", className)}
        onClick={(event) => event.stopPropagation()}
        onMouseUp={handleMouseUpByBlurring}
        tabIndex={-1}
        id={id}
        style={{
          transform: `translate3d(${this.popoverPosition?.overlay.left || 0}px, ${
            this.popoverPosition?.overlay.top || 0
          }px, 0)`,
        }}
      >
        <Panel
          theme={theme === PopoverTheme.Dark ? PanelTheme.Dark : PanelTheme.Light}
          className={classNames(
            "popover__panel",
            `popover__panel--${theme}`,
            `popover__panel--${status}`,
            `popover__panel--${size}`,
            `popover__panel--${this.position}`,
          )}
        >
          {!!caretSize && (
            <div
              className={classNames("popover__caret", `popover__caret--${this.position}`, `popover__caret--${theme}`)}
              style={{
                ...this.popoverPosition?.caret,
                width: caretSize,
                height: caretSize,
              }}
            />
          )}
          <div className="popover__panel-scroll" style={{ maxHeight: this.props.maxHeight }}>
            <div className="popover__panel-content">{children}</div>
          </div>
        </Panel>
      </div>
    );
  }
}

export default Popover;
